import api from '.';
import {
  Account,
  PaginatedAccounts,
  paginationQueryParams,
  PaginationParams,
  PushNotificationToken,
} from '@karehero/models';

export const accountApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllAccount: builder.query<PaginatedAccounts, PaginationParams | void>({
      query: (params) => ({
        url: '/account',
        params: paginationQueryParams(params),
      }),
      providesTags: ['Accounts'],
    }),
    getAccount: builder.query<Account, { accountId: string }>({
      query: ({ accountId }) => `/account/${accountId}`,
      providesTags: ['Accounts'],
    }),
    createAccount: builder.mutation<Account, Account>({
      query: (account) => ({
        url: '/account',
        method: 'POST',
        body: account,
      }),
      invalidatesTags: ['Accounts'],
    }),
    deleteAccount: builder.mutation<Account, { id: string }>({
      query: ({ id }) => ({
        url: `/account/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Accounts', 'CareCircles', 'OrganizationMembers'],
    }),
    registerPushNotificationToken: builder.mutation<
      PushNotificationToken,
      PushNotificationToken
    >({
      query: (pushNotificationToken) => ({
        url: `/account/notification-token`,
        method: 'POST',
        body: pushNotificationToken,
      }),
    }),
    verifyAccount: builder.mutation<
      Account,
      { accountID: string; organizationID: string; roleID: string }
    >({
      query: ({ accountID, organizationID, roleID }) => ({
        url: `/account/${accountID}/verify`,
        method: 'POST',
        body: {
          organizationID,
          roleID,
        },
      }),
      invalidatesTags: ['Accounts'],
    }),
    updateAccountAvatar: builder.mutation<
      Account,
      { accountId: string; avatarConfig: string; avatarUrl: string }
    >({
      query: ({ accountId, ...body }) => ({
        url: `/account/${accountId}/avatar`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Accounts', 'CareCircles'],
    }),
    updateAccountConsent: builder.mutation<
      Account,
      { consents: Record<string, boolean> }
    >({
      query: (body) => ({
        url: '/account/consent',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Accounts'],
    }),
    upgradeAccountRole: builder.mutation<
      Account,
      {
        accountID: string;
        role: string;
        organizationID?: string;
        partnerID?: string;
      }
    >({
      query: ({ accountID, ...body }) => ({
        url: `/account/${accountID}/role`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Accounts'],
    }),
  }),
});

export const {
  useGetAllAccountQuery,
  useGetAccountQuery,
  useCreateAccountMutation,
  useDeleteAccountMutation,
  useRegisterPushNotificationTokenMutation,
  useVerifyAccountMutation,
  useUpdateAccountAvatarMutation,
  useUpdateAccountConsentMutation,
  useUpgradeAccountRoleMutation,
} = accountApi;

export default accountApi;
